const SYSTEM_LABELS = {
  hubspot: 'HubSpot',
  salesforce: 'Salesforce',
  quickbooks: 'QuickBooks',
  xero: 'Xero',
};

function hubSpotDealUrl(portalId, dealId) {
  return `https://app.hubspot.com/contacts/${portalId}/deal/${dealId}`;
}

function hubSpotInvoiceUrl(portalId, invoiceId) {
  return `https://app.hubspot.com/contacts/${portalId}/record/0-53/${invoiceId}`;
}

function quickBooksInvoiceUrl(txnId, realmId) {
  const params = new URLSearchParams({ txnId: String(txnId) });
  if (realmId != null && realmId !== '') {
    params.set('companyId', String(realmId));
  }
  return `https://app.qbo.intuit.com/app/invoice?${params.toString()}`;
}

function salesforceRecordUrl(recordId) {
  return `https://lightning.force.com/lightning/r/Opportunity/${recordId}/view`;
}

function xeroInvoiceUrl(invoiceId) {
  return `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${encodeURIComponent(invoiceId)}`;
}

export function normalizeSystemKey(system) {
  if (system == null || system === '') return null;
  const key = String(system).trim().toLowerCase();
  if (key === 'qbo' || key === 'quickbooks online') return 'quickbooks';
  if (key.includes('hubspot') || key === 'hs') return 'hubspot';
  if (key.includes('salesforce') || key === 'sf') return 'salesforce';
  if (key.includes('xero')) return 'xero';
  return key;
}

export function formatSystemLabel(system) {
  const key = normalizeSystemKey(system);
  if (!key) return '—';
  return SYSTEM_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

function pickPortalId(event, company) {
  return (
    event?.event_data?.portalId
    ?? event?.event_data?.portal_id
    ?? company?.crm_portal_id
    ?? company?.portal_id
    ?? company?.portalId
    ?? null
  );
}

function pickRealmId(company) {
  return (
    company?.acct_realm_id
    ?? company?.realm_id
    ?? company?.realmId
    ?? company?.qb_realm_id
    ?? null
  );
}

function isValidRecordId(id) {
  if (id == null || id === '') return false;
  const value = String(id).trim();
  return value !== 'XXX' && value !== '—';
}

function isHubSpotInvoiceEvent(event) {
  const objectTypeId = event.event_data?.objectTypeId ?? '';
  const subscriptionType = event.event_data?.subscriptionType ?? '';
  return objectTypeId === '0-53'
    || subscriptionType.includes('invoice')
    || /invoice/i.test(event.event_type ?? '');
}

export function getHubSpotEventUrl(event, company) {
  if (!event) return null;

  const direct = event.hubspot_url ?? event.source_url ?? event.crm_url;
  if (direct) return direct;

  const portalId = pickPortalId(event, company);
  if (portalId == null || portalId === '') return null;

  const dealId = event.object_id ?? event.event_data?.objectId;
  const invoiceId =
    event.source_doc_id
    ?? (isHubSpotInvoiceEvent(event) ? (event.object_id ?? event.event_data?.objectId) : null)
    ?? event.stream_data?.inv_data?.hs_object_id
    ?? event.stream_data?.inv_data?.id
    ?? event.stream_data?.inv_data?.hs_id;

  if (invoiceId != null && invoiceId !== '' && isHubSpotInvoiceEvent(event)) {
    return hubSpotInvoiceUrl(portalId, invoiceId);
  }
  if (dealId != null && dealId !== '' && isValidRecordId(dealId)) {
    return hubSpotDealUrl(portalId, dealId);
  }
  return null;
}

export function getSalesforceEventUrl(event) {
  if (!event) return null;

  const direct = event.salesforce_url ?? event.crm_url ?? event.source_url;
  if (direct) return direct;

  const recordId = event.source_doc_id ?? event.object_id ?? event.event_data?.objectId;
  if (!isValidRecordId(recordId)) return null;

  return salesforceRecordUrl(recordId);
}

export function getQuickBooksEventUrl(event, company) {
  if (!event) return null;

  const direct = event.quickbooks_url ?? event.target_url ?? event.acct_url;
  if (direct) return direct;

  const txnId = event.target_doc_id;
  if (txnId == null || txnId === '') return null;

  return quickBooksInvoiceUrl(txnId, pickRealmId(company));
}

export function getXeroEventUrl(event) {
  if (!event) return null;

  const direct = event.xero_url ?? event.target_url ?? event.acct_url;
  if (direct) return direct;

  const invoiceId = event.target_doc_id;
  if (invoiceId == null || invoiceId === '') return null;

  return xeroInvoiceUrl(invoiceId);
}

export function getCrmEventUrl(event, company) {
  if (!event) return null;

  const system = normalizeSystemKey(event.source_system);
  if (system === 'hubspot') return getHubSpotEventUrl(event, company);
  if (system === 'salesforce') return getSalesforceEventUrl(event);
  if (!system) return getHubSpotEventUrl(event, company) ?? getSalesforceEventUrl(event);
  return null;
}

export function getAccountingEventUrl(event, company) {
  if (!event) return null;

  const system = normalizeSystemKey(event.target_system);
  if (system === 'quickbooks') return getQuickBooksEventUrl(event, company);
  if (system === 'xero') return getXeroEventUrl(event);
  if (!system) return getQuickBooksEventUrl(event, company) ?? getXeroEventUrl(event);
  return null;
}

export function getCrmLinkLabel(event) {
  return `View in ${formatSystemLabel(event?.source_system ?? 'hubspot')}`;
}

export function getAccountingLinkLabel(event) {
  return `View in ${formatSystemLabel(event?.target_system ?? 'quickbooks')}`;
}
